<div class="container">
  <sca-module-title class="title-test">
    <h1>Traces Playground</h1>
    <span scaModuleTitlePost>Testing de @argo/traces - {{ coverage }}% cobertura</span>
  </sca-module-title>

  <!-- Estado de Envío -->
  <section class="card">
    <h2>Estado de Envío (TracesPhase)</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      TracesStore gestiona el envío de trazas de log al backend con estado reactivo
    </sca-alert>
    
    <div class="status-grid">
      <sca-card-workshop [selected]="tracesStore.phase$() === CommonPhase.IDLE">
        <header scaCardWorkshopHeader>
          <sca-badge variant="default" [rounded]="false">IDLE</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Sin actividad</p>
        <p scaCardWorkshopAddress>Estado inicial, sin trazas enviándose</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="tracesStore.phase$() === TracesPhase.SENDING">
        <header scaCardWorkshopHeader>
          <sca-badge variant="warning" [rounded]="false">SENDING</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Enviando</p>
        <p scaCardWorkshopAddress>POST en curso al backend</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="tracesStore.phase$() === TracesPhase.SENT">
        <header scaCardWorkshopHeader>
          <sca-badge variant="success" [rounded]="false">SENT</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Enviado OK</p>
        <p scaCardWorkshopAddress>Trace recibido correctamente</p>
      </sca-card-workshop>

      <sca-card-workshop [selected]="tracesStore.phase$() === CommonPhase.ERROR">
        <header scaCardWorkshopHeader>
          <sca-badge variant="error" [rounded]="false">ERROR</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Error</p>
        <p scaCardWorkshopAddress>{{ tracesStore.error$() || 'Envío fallido' }}</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Computed Properties -->
  <section class="card">
    <h2>Computed Properties</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Señales reactivas computadas del TracesStore
    </sca-alert>
    
    <table class="properties-table">
      <thead>
        <tr>
          <th>Propiedad</th>
          <th>Tipo</th>
          <th>Valor Actual</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><code>phase$()</code></td>
          <td>Signal&lt;TracesPhase&gt;</td>
          <td><span class="value-enum">{{ tracesStore.phase$() }}</span></td>
          <td>Fase actual: IDLE | SENDING | SENT | ERROR</td>
        </tr>
        <tr>
          <td><code>error$()</code></td>
          <td>Signal&lt;string|null&gt;</td>
          <td><span [class]="tracesStore.error$() ? 'value-warning' : 'value-null'">{{ tracesStore.error$() || 'null' }}</span></td>
          <td>Último error en envío de traces</td>
        </tr>
        <tr>
          <td><code>isSending$()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="tracesStore.isSending$() ? 'value-true' : 'value-false'">{{ tracesStore.isSending$() }}</span></td>
          <td>Indica si hay envío en curso</td>
        </tr>
        <tr>
          <td><code>isSent$()</code></td>
          <td>Computed&lt;boolean&gt;</td>
          <td><span [class]="tracesStore.isSent$() ? 'value-true' : 'value-false'">{{ tracesStore.isSent$() }}</span></td>
          <td>Indica si último envío fue exitoso</td>
        </tr>
      </tbody>
    </table>
  </section>

  <!-- Método Público -->
  <section class="card">
    <h2>Método Público</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Método principal para enviar trazas al backend
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="bug_report" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>sendTrace(trace: SendTrace): Promise&lt;boolean&gt;</p>
        <p scaCardWorkshopAddress>
          Envía una traza al backend mediante POST<br>
          Cambia fase a SENDING → SENT o ERROR<br>
          Retorna Promise&lt;boolean&gt; según éxito/fallo
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Test de Envío de Traces -->
  <section class="card">
    <h2>🧪 Test de Envío de Traces</h2>
    
    @if (!authStore.logged()) {
      <sca-alert variant="warning" style="margin-bottom: 1.5rem;">
        <sca-icon icon="warning" size="24px" [weight]="300" left />
        <strong>Autenticación Requerida:</strong> Para enviar traces necesitas estar autenticado.
        Ve a <a routerLink="/test/authentication" style="color: #d63384; font-weight: bold;">Authentication Playground</a> para iniciar sesión.
      </sca-alert>
    } @else {
      <sca-alert variant="success" style="margin-bottom: 1.5rem;">
        <sca-icon icon="check_circle" size="24px" [weight]="300" left />
        <strong>Autenticado:</strong> {{ authStore.user().preferred_username || authStore.user().email }}
      </sca-alert>
    }
    
    <div class="test-section" [class.disabled]="!authStore.logged()">
      <h3>Formulario de Trace Personalizado</h3>
      
      <div class="form-grid">
        <sca-text-field label="Service (opcional)">
          <input type="text" scaNativeControl [(ngModel)]="traceForm.service" placeholder="nombre-del-servicio">
        </sca-text-field>
        
        <sca-text-field label="Operation (opcional)">
          <input type="text" scaNativeControl [(ngModel)]="traceForm.operation" placeholder="nombre-operacion">
        </sca-text-field>
        
        <sca-text-field label="Message (requerido)" class="full-width">
          <textarea scaNativeControl [(ngModel)]="traceForm.message" placeholder="Descripción del trace..." rows="3"></textarea>
        </sca-text-field>
        
        <sca-text-field label="Exception (opcional)" class="full-width">
          <textarea scaNativeControl [(ngModel)]="traceForm.exception" placeholder="Stack trace o detalles del error..." rows="2"></textarea>
        </sca-text-field>
        
        <sca-text-field label="Log Level (requerido)">
          <select scaNativeControl [(ngModel)]="traceForm.logLevelType">
            <option [value]="LogLevel.FATAL">🔴 FATAL - Errores críticos</option>
            <option [value]="LogLevel.ERROR">🟠 ERROR - Errores</option>
            <option [value]="LogLevel.WARN">🟡 WARN - Advertencias</option>
            <option [value]="LogLevel.INFO">🔵 INFO - Información</option>
            <option [value]="LogLevel.DEBUG">🟣 DEBUG - Depuración</option>
            <option [value]="LogLevel.TRACE">⚪ TRACE - Trazado detallado</option>
          </select>
        </sca-text-field>
      </div>
      
      <button 
        sca-button
        (click)="sendCustomTrace()" 
        [disabled]="!traceForm.message || tracesStore.isSending$() || !authStore.logged()">
        {{ tracesStore.isSending$() ? '⏳ Enviando...' : '📤 Enviar Trace' }}
      </button>
      
      @if (sendResult) {
        <sca-alert [variant]="sendResultClass === 'success' ? 'success' : 'error'" style="margin-top: 1rem;">
          <sca-icon [icon]="sendResultClass === 'success' ? 'check_circle' : 'error'" size="24px" [weight]="300" left />
          {{ sendResult }}
        </sca-alert>
      }
    </div>
    
    <div class="quick-tests" style="margin-top: 2rem;">
      <h3>🚀 Traces Rápidos de Prueba</h3>
      <sca-alert variant="info" style="margin-bottom: 1rem;">
        <sca-icon icon="info" size="24px" [weight]="300" left />
        Botones rápidos para enviar traces de prueba con diferentes niveles
      </sca-alert>
      
      <div class="quick-buttons">
        <button 
          sca-button
          (click)="sendQuickTrace(LogLevel.INFO, 'Test INFO trace')" 
          [disabled]="!authStore.logged()">
          INFO Test
        </button>
        <button 
          sca-button
          (click)="sendQuickTrace(LogLevel.WARN, 'Test WARNING trace')" 
          [disabled]="!authStore.logged()">
          WARN Test
        </button>
        <button 
          sca-button
          (click)="sendQuickTrace(LogLevel.ERROR, 'Test ERROR trace')" 
          [disabled]="!authStore.logged()">
          ERROR Test
        </button>
      </div>
    </div>
  </section>

  <!-- LogLevel Enum -->
  <section class="card">
    <h2>LogLevel (Niveles de Severidad)</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      6 niveles de severidad para clasificar trazas
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="default" [rounded]="false">{{ LogLevel.TRACE }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>TRACE</p>
        <p scaCardWorkshopAddress>Información muy detallada, trazas de ejecución</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="default" [rounded]="false">{{ LogLevel.DEBUG }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>DEBUG</p>
        <p scaCardWorkshopAddress>Información de depuración técnica</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="success" [rounded]="false">{{ LogLevel.INFO }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>INFO</p>
        <p scaCardWorkshopAddress>Información general del flujo normal</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="warning" [rounded]="false">{{ LogLevel.WARN }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>WARN</p>
        <p scaCardWorkshopAddress>Advertencias que no impiden funcionamiento</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="error" [rounded]="false">{{ LogLevel.ERROR }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>ERROR</p>
        <p scaCardWorkshopAddress>Errores que afectan funcionalidad</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="error" [rounded]="false">{{ LogLevel.FATAL }}</sca-badge>
        </header>
        <p scaCardWorkshopTitle>FATAL</p>
        <p scaCardWorkshopAddress>Errores críticos que detienen la aplicación</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- ChannelType Enum -->
  <section class="card">
    <h2>ChannelType (Canales de Salida)</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      3 canales de destino para enviar trazas
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="smartphone" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>{{ ChannelType.mobile }}</p>
        <p scaCardWorkshopAddress>
          Canal MOBILE<br>
          <small>Para aplicaciones móviles</small>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="web" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>{{ ChannelType.web }}</p>
        <p scaCardWorkshopAddress>
          Canal WEB<br>
          <small>Para aplicaciones web</small>
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="dns" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>{{ ChannelType.ssr }}</p>
        <p scaCardWorkshopAddress>
          Canal SSR<br>
          <small>Para server-side rendering</small>
        </p>
      </sca-card-workshop>


    </div>
  </section>

  <!-- SendTrace Interface -->
  <section class="card">
    <h2>SendTrace Interface</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Estructura de datos para enviar una traza
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="article" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>message: string</p>
        <p scaCardWorkshopAddress>Mensaje descriptivo del evento o error</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="label" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>level: LogLevel</p>
        <p scaCardWorkshopAddress>Severidad: VERBOSE | DEBUG | INFO | WARN | ERROR | FATAL</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="source" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>source?: string</p>
        <p scaCardWorkshopAddress>Origen: nombre de componente, servicio o módulo</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="data_object" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>metadata?: any</p>
        <p scaCardWorkshopAddress>Datos adicionales: stack trace, contexto, variables</p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="alt_route" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>channels?: ChannelType[]</p>
        <p scaCardWorkshopAddress>Destinos: mobile | web | ssr</p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Configuración -->
  <section class="card">
    <h2>Configuración y Providers</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Configuración del módulo de trazas
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="settings" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>provideTracesConfig()</p>
        <p scaCardWorkshopAddress>
          Provider para app.config.ts<br>
          Configura endpoint, canales por defecto<br>
          Actualmente stub: sin opciones configurables
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="link" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Endpoint Backend</p>
        <p scaCardWorkshopAddress>
          Se obtiene de ConfigStore<br>
          URL: config.api.traces o config.api.baseUrl + '/traces'<br>
          POST con body: SendTrace
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="security" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Autenticación</p>
        <p scaCardWorkshopAddress>
          Usa AuthenticationStore<br>
          Si hay token: añade Authorization header<br>
          Permite envío sin auth (público)
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="cached" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>Retry Logic</p>
        <p scaCardWorkshopAddress>
          Sin retry automático actual<br>
          Recomendado: implementar exponential backoff<br>
          Considerar offline queue con Service Worker
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Arquitectura del Módulo -->
  <section class="card">
    <h2>Arquitectura del Módulo</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Estructura de archivos del módulo @argo/traces
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="store" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>traces.store.ts</p>
        <p scaCardWorkshopAddress>
          Store principal con sendTrace()<br>
          Gestiona fase SENDING/SENT/ERROR<br>
          POST al backend con HttpClient
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="category" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>models/</p>
        <p scaCardWorkshopAddress>
          log-level.enum.ts: 6 niveles<br>
          channel-type.enum.ts: 5 canales<br>
          send-trace.interface.ts: estructura datos
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="extension" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>traces.providers.ts</p>
        <p scaCardWorkshopAddress>
          provideTracesConfig()<br>
          Inyecta TracesStore<br>
          Preparado para opciones futuras
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-icon icon="api" size="24" [weight]="400" />
        </header>
        <p scaCardWorkshopTitle>public-api.ts</p>
        <p scaCardWorkshopAddress>
          Exports: TracesStore, LogLevel, ChannelType<br>
          SendTrace, TracesPhase, provideTracesConfig<br>
          API pública del módulo
        </p>
      </sca-card-workshop>
    </div>
  </section>

  <!-- Casos de Uso -->
  <section class="card">
    <h2>Casos de Uso Recomendados</h2>
    
    <sca-alert variant="info" style="margin-bottom: 1.5rem;">
      <sca-icon icon="info" size="24px" [weight]="300" left />
      Escenarios típicos donde usar TracesStore
    </sca-alert>
    
    <div class="utilities-grid">
      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="error" [rounded]="false">ERROR</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Global Error Handler</p>
        <p scaCardWorkshopAddress>
          Capturar errores JavaScript no controlados<br>
          window.onerror, ErrorHandler de Angular<br>
          Enviar stack trace completo al backend
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="warning" [rounded]="false">WARN</sca-badge>
        </header>
        <p scaCardWorkshopTitle>HTTP Interceptors</p>
        <p scaCardWorkshopAddress>
          Loguear requests fallidos (4xx, 5xx)<br>
          Detectar timeout o network errors<br>
          Analizar patrones de fallo
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="success" [rounded]="false">INFO</sca-badge>
        </header>
        <p scaCardWorkshopTitle>User Journey Tracking</p>
        <p scaCardWorkshopAddress>
          Login, logout, cambio de pantalla<br>
          Acciones clave del usuario<br>
          Reconstruir sesión en caso de error
        </p>
      </sca-card-workshop>

      <sca-card-workshop>
        <header scaCardWorkshopHeader>
          <sca-badge variant="default" [rounded]="false">DEBUG</sca-badge>
        </header>
        <p scaCardWorkshopTitle>Performance Monitoring</p>
        <p scaCardWorkshopAddress>
          Tiempos de carga, renderizado<br>
          Detección de memory leaks<br>
          Optimización de componentes pesados
        </p>
      </sca-card-workshop>
    </div>
  </section>
</div>
